import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If already authenticated, bounce straight to the dashboard
  if (user) return <Navigate to="/app" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success(t("auth.toast_logged"));
      navigate("/app");
    } else {
      setError(r.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cosmos" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between text-white p-12 relative overflow-hidden bg-cosmos-dim">
        <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
          <span className="block w-2 h-2 rounded-full pulse-dot" style={{ background: "linear-gradient(135deg,#8B5CF6,#3B82F6)" }}></span>
          <span className="font-display font-bold text-2xl text-white">
            Open<span className="brand-text-light">Payments</span>
          </span>
        </Link>
        <div className="relative z-10">
          <h2 className="font-display font-bold text-4xl tracking-tighter leading-tight text-white">
            {t("auth.left_title")}
          </h2>
          <p className="text-white/70 mt-4 max-w-md">
            {t("auth.left_lead")}
          </p>
        </div>
        <div className="font-mono-fin text-xs text-white/50 relative z-10">
          FCA Ref. 9XX834 · MiCA · SEPA SCT Inst
        </div>
        <div className="absolute -right-20 -bottom-20 w-[400px] h-[400px] border border-purple-300/20 rounded-full"></div>
        <div className="absolute right-10 top-1/3 w-[200px] h-[200px] border border-blue-300/30 rounded-full"></div>
        <div className="absolute -left-10 top-20 w-[150px] h-[150px] bg-purple-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="flex flex-col justify-center p-8 lg:p-16 max-w-md mx-auto w-full bg-white/95 backdrop-blur-2xl lg:bg-white relative">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher tone="dark" />
        </div>
        <div className="lg:hidden mb-8">
          <Link to="/" className="flex items-center gap-2" data-testid="login-mobile-logo">
            <span className="block w-2 h-2 rounded-full" style={{ background: "linear-gradient(135deg,#8B5CF6,#3B82F6)" }}></span>
            <span className="font-display font-bold text-xl text-slate-900">
              Open<span className="brand-text">Payments</span>
            </span>
          </Link>
        </div>

        <div className="font-mono-fin text-xs uppercase tracking-widest brand-text font-bold mb-3">{t("auth.login_eyebrow")}</div>
        <h1 className="font-display font-bold text-4xl tracking-tighter text-slate-900">{t("auth.login_title")}</h1>
        <p className="text-slate-500 mt-3">{t("auth.login_subtitle")}</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("auth.email")}</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="w-full mt-2 px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-600 focus:ring-4 focus:ring-purple-100 outline-none text-slate-900"
              placeholder="tu@email.com"
            />
          </div>
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("auth.password")}</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="w-full mt-2 px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-600 focus:ring-4 focus:ring-purple-100 outline-none text-slate-900 font-mono-fin"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">
            {loading ? t("auth.loading") : <>{t("auth.submit")} <ArrowRight size={18} /></>}
          </button>
        </form>

        <div className="mt-8 text-sm text-slate-500">
          {t("auth.no_account")}{" "}
          <Link to="/registro" className="brand-text font-bold hover:underline" data-testid="login-to-register">
            {t("auth.create")}
          </Link>
        </div>
      </div>
    </div>
  );
}
